<template>
  <div class="asset-registration">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <button
          class="back-btn"
          @click="$emit('update:modelValue', 'provider-assets')"
        >
          <svg class="back-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M12 19l-7-7 7-7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Back to Catalog
        </button>
        <div class="header-title">
          <div class="title-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M9 17V7M9 7L5 11M9 7L13 11"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M15 7V17M15 17L11 13M15 17L19 13"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div v-if="!editAsset" class="title-content">
            <h1>Register New Asset</h1>
            <p>Add a new data asset to your catalog</p>
          </div>
          <div v-else class="title-content">
            <h1>{{ viewOnly ? "View Asset" : "Edit Asset" }}</h1>
            <p>
              {{
                viewOnly
                  ? "View the published asset details"
                  : "Edit asset for your catalog"
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Form -->
    <div class="registration-container">
      <div class="form-card">
        <div class="card-header">
          <h2>Asset Details</h2>
          <p>Fill in the information about your data asset</p>
        </div>

        <div class="form-content">
          <fieldset :disabled="viewOnly" class="asset-fields">
            <!-- Basic Information -->
            <div class="form-section">
              <h3 class="section-title">Basic Information</h3>
              <div class="form-grid">
                <div class="form-group">
                  <label class="form-label">
                    Asset Name <span class="required">*</span>
                  </label>
                  <input
                    v-model="newAsset.name"
                    type="text"
                    class="form-input"
                    required
                  />
                </div>

                <div class="form-group">
                  <label class="form-label"> Owner </label>
                  <input
                    v-model="user"
                    type="text"
                    class="form-input"
                    readonly
                  />
                </div>

                <div class="form-group">
                  <label class="form-label"> Asset Version </label>
                  <input
                    v-model="newAsset.assetVersion"
                    type="text"
                    class="form-input"
                    placeholder="v0.0.0"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label"> Title </label>
                  <input
                    v-model="newAsset.title"
                    type="text"
                    class="form-input"
                    placeholder="Example data product"
                  />
                </div>

                <div class="form-group full-width">
                  <label class="form-label"
                    >Description <span class="required">*</span></label
                  >
                  <textarea
                    v-model="newAsset.description"
                    rows="5"
                    class="form-textarea"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Creator -->
            <div class="form-section">
              <button
                type="button"
                class="section-toggle"
                @click="creatorOpen = !creatorOpen"
              >
                <span>Creator Information</span>
                <svg
                  class="toggle-icon"
                  :class="{ open: creatorOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <transition name="fade-slide">
                <div v-if="creatorOpen" class="advanced-panel">
                  <div class="form-grid">
                    <div class="form-group">
                      <label class="form-label"> Legal Name </label>
                      <input
                        v-model="newAsset.creatorLegalName"
                        type="text"
                        class="form-input"
                        placeholder="Example Creator Organization Name"
                      />
                    </div>

                    <div class="form-group">
                      <label class="form-label"> Contact Name </label>
                      <input
                        v-model="newAsset.creatorContactName"
                        type="text"
                        class="form-input"
                        placeholder="Example Creator Contact Name"
                      />
                    </div>

                    <div class="form-group">
                      <label class="form-label"> Email </label>
                      <input
                        v-model="newAsset.creatorEmail"
                        type="email"
                        class="form-input"
                        placeholder="mailto:creator@example.org"
                      />
                    </div>

                    <div class="form-group">
                      <label class="form-label"> URL </label>
                      <input
                        v-model="newAsset.creatorUrl"
                        type="url"
                        class="form-input"
                        placeholder="https://creator.example.org"
                      />
                    </div>

                    <div class="form-group">
                      <label class="form-label"> Participant UID </label>
                      <input
                        v-model="newAsset.creatorUid"
                        type="text"
                        class="form-input"
                        placeholder="Unique ID of participant"
                      />
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Dates & Tags -->
            <div class="form-section">
              <button
                type="button"
                class="section-toggle"
                @click="datesOpen = !datesOpen"
              >
                <span>Dates & Tags</span>
                <svg
                  class="toggle-icon"
                  :class="{ open: datesOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <transition name="fade-slide">
                <div v-if="datesOpen" class="advanced-panel">
                  <div class="form-grid">
                    <div class="form-group" v-if="!editAsset">
                      <label class="form-label"> Created Date </label>
                      <input
                        v-model="newAsset.created"
                        type="date"
                        class="form-input"
                      />
                    </div>

                    <div class="form-group" v-else>
                      <label class="form-label"> Updated Date </label>
                      <input
                        v-model="newAsset.updated"
                        type="date"
                        class="form-input"
                      />
                    </div>

                    <div class="form-group full-width">
                      <label class="form-label"> Tags (comma-separated) </label>
                      <input
                        v-model="newAsset.tags"
                        type="text"
                        class="form-input"
                        placeholder="environmental, air-quality, CO2"
                      />
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Formats & Dataspace -->
            <div class="form-section">
              <button
                type="button"
                class="section-toggle"
                @click="formatsOpen = !formatsOpen"
              >
                <span>Formats & Dataspace</span>
                <svg
                  class="toggle-icon"
                  :class="{ open: formatsOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <transition name="fade-slide">
                <div v-if="formatsOpen" class="advanced-panel">
                  <div class="form-grid">
                    <div class="form-group">
                      <label class="form-label"> Format </label>
                      <div class="select-wrapper">
                        <select
                          v-model="newAsset.format"
                          placeholder="application/json"
                          class="form-input"
                        >
                          <option value="application/json">
                            application/json
                          </option>
                        </select>
                        <div class="select-arrow">▼</div>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label"> Content Type </label>
                      <div class="select-wrapper">
                        <select
                          v-model="newAsset.contentType"
                          placeholder="application/json"
                          class="form-input"
                        >
                          <option value="application/json">
                            application/json
                          </option>
                        </select>
                        <div class="select-arrow">▼</div>
                      </div>
                    </div>

                    <!-- <div class="form-group">
                    <label class="form-label"> Dataspace Name </label>
                    <input
                      v-model="newAsset.dataSpaceName"
                      type="text"
                      class="form-input"
                      placeholder="Example name of the original hosting dataspace"
                    />
                  </div>

                  <div class="form-group">
                    <label class="form-label"> Dataspace ID </label>
                    <input
                      v-model="newAsset.dataSpaceId"
                      type="text"
                      class="form-input"
                      placeholder="Example ID of original hosting dataspace"
                    />
                  </div> -->
                  </div>
                </div>
              </transition>
            </div>

            <!-- Pricing -->
            <div class="form-section">
              <button
                type="button"
                class="section-toggle"
                @click="pricingOpen = !pricingOpen"
              >
                <span>Pricing</span>
                <svg
                  class="toggle-icon"
                  :class="{ open: pricingOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <transition name="fade-slide">
                <div v-if="pricingOpen" class="advanced-panel">
                  <div class="form-grid">
                    <div class="form-group">
                      <label class="form-label"> Price </label>
                      <input
                        v-model="newAsset.price"
                        type="text"
                        class="form-input"
                        placeholder="2.0"
                      />
                    </div>

                    <div class="form-group">
                      <label class="form-label"> Unit </label>
                      <div class="select-wrapper">
                        <select v-model="newAsset.priceUnit" class="form-input">
                          <option value="OTP">OTP</option>
                        </select>
                        <div class="select-arrow">▼</div>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label"> Currency </label>
                      <div class="select-wrapper">
                        <select
                          v-model="newAsset.priceCurrency"
                          class="form-input"
                        >
                          <option value="EUR">EUR</option>
                        </select>
                        <div class="select-arrow">▼</div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Private Properties -->
            <div class="form-section">
              <button
                type="button"
                class="section-toggle"
                @click="privateOpen = !privateOpen"
              >
                <span>Private Properties</span>
                <svg
                  class="toggle-icon"
                  :class="{ open: privateOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <transition name="fade-slide">
                <div v-if="privateOpen" class="advanced-panel">
                  <div class="form-grid">
                    <div class="form-group full-width">
                      <label class="form-label"> internalNotes </label>
                      <textarea
                        v-model="newAsset.privateNotes"
                        rows="3"
                        class="form-textarea"
                        placeholder="Provider-only notes (not exposed in catalog)."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Data Address -->
            <div class="form-section">
              <h3 class="section-title">Data Address</h3>
              <div class="form-grid">
                <div class="form-group">
                  <label class="form-label"> Data Address Type </label>
                  <div class="select-wrapper">
                    <select
                      v-model="newAsset.dataAddressType"
                      class="form-input"
                    >
                      <option value="HttpData">HttpData</option>
                      <option value="Database">Database</option>
                    </select>
                    <div class="select-arrow">▼</div>
                  </div>
                </div>

                <div
                  class="form-group full-width"
                  v-if="newAsset.dataAddressType === 'HttpData'"
                >
                  <label class="form-label">
                    Endpoint URL <span class="required">*</span>
                  </label>
                  <input
                    v-model="newAsset.endpoint"
                    type="url"
                    placeholder="https://api.example.com/data/orders"
                    class="form-input"
                    required
                  />
                </div>
                <div
                  class="form-group full-width"
                  v-else-if="newAsset.dataAddressType === 'Database'"
                >
                  <!-- Database provider controls which backend connector is used. -->
                  <label class="form-label">Database provider</label>
                  <div class="select-wrapper database-provider-select">
                    <select
                      v-model="newAsset.databaseProvider"
                      class="form-input"
                      @change="changeDatabaseProvider"
                    >
                      <option value="dlm">DLM Store</option>
                      <option value="mongodb">MongoDB</option>
                      <option value="postgresql">PostgreSQL</option>
                    </select>
                    <div class="select-arrow">▼</div>
                  </div>

                  <!-- Mongo credentials stay in page memory and are sent only to
                     the connect/preview routes; they are not saved in the asset. -->
                  <div
                    v-if="newAsset.databaseProvider === 'mongodb'"
                    class="database-connection-card"
                  >
                    <div class="connection-card-heading">
                      <div>
                        <h4>Connect to MongoDB</h4>
                        <p>
                          Enter a connection URI and the database you want to
                          browse.
                        </p>
                      </div>
                      <span v-if="mongoConnected" class="connection-badge"
                        >Connected</span
                      >
                    </div>
                    <div class="form-grid">
                      <div class="form-group full-width">
                        <label class="form-label"
                          >Connection URI <span class="required">*</span></label
                        >
                        <input
                          v-model="mongoConnection.connectionUri"
                          type="password"
                          class="form-input"
                          placeholder="mongodb://username:password@host:27017"
                          autocomplete="new-password"
                          @input="markMongoDisconnected"
                        />
                        <span class="input-hint"
                          >MongoDB Atlas mongodb+srv:// URIs are also
                          supported.</span
                        >
                      </div>
                      <div class="form-group">
                        <label class="form-label"
                          >Database name <span class="required">*</span></label
                        >
                        <input
                          v-model="mongoConnection.databaseName"
                          type="text"
                          class="form-input"
                          placeholder="catalog"
                          @input="markMongoDisconnected"
                        />
                      </div>
                      <div class="form-group connection-action">
                        <button
                          type="button"
                          class="btn btn-primary btn-compact"
                          :disabled="isConnectingMongo || !canConnectMongo"
                          @click="connectMongoDB"
                        >
                          {{
                            isConnectingMongo
                              ? "Connecting..."
                              : mongoConnected
                              ? "Reconnect"
                              : "Test and connect"
                          }}
                        </button>
                      </div>
                    </div>
                    <div
                      v-if="mongoConnectionStatus.message"
                      class="inline-status"
                      :class="mongoConnectionStatus.type"
                    >
                      {{ mongoConnectionStatus.message }}
                    </div>
                  </div>

                  <!-- PostgreSQL credentials also stay only in this component.
                     The password is never copied into the asset payload. -->
                  <div
                    v-else-if="newAsset.databaseProvider === 'postgresql'"
                    class="database-connection-card"
                  >
                    <div class="connection-card-heading">
                      <div>
                        <h4>Connect to PostgreSQL</h4>
                        <p>
                          Enter the server details and schema you want to
                          browse.
                        </p>
                      </div>
                      <span v-if="postgresConnected" class="connection-badge"
                        >Connected</span
                      >
                    </div>
                    <div class="form-grid">
                      <div class="form-group">
                        <label class="form-label"
                          >Host <span class="required">*</span></label
                        >
                        <input
                          v-model="postgresConnection.host"
                          type="text"
                          class="form-input"
                          placeholder="host.docker.internal"
                          @input="markPostgresDisconnected"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label"
                          >Port <span class="required">*</span></label
                        >
                        <input
                          v-model.number="postgresConnection.port"
                          type="number"
                          min="1"
                          max="65535"
                          class="form-input"
                          placeholder="5432"
                          @input="markPostgresDisconnected"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label"
                          >Database name <span class="required">*</span></label
                        >
                        <input
                          v-model="postgresConnection.databaseName"
                          type="text"
                          class="form-input"
                          placeholder="localcatalog_test"
                          @input="markPostgresDisconnected"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label"
                          >Schema <span class="required">*</span></label
                        >
                        <input
                          v-model="postgresConnection.schemaName"
                          type="text"
                          class="form-input"
                          placeholder="public"
                          @input="markPostgresDisconnected"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label"
                          >Username <span class="required">*</span></label
                        >
                        <input
                          v-model="postgresConnection.username"
                          type="text"
                          class="form-input"
                          placeholder="postgres"
                          autocomplete="username"
                          @input="markPostgresDisconnected"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Password</label>
                        <input
                          v-model="postgresConnection.password"
                          type="password"
                          class="form-input"
                          placeholder="Database password"
                          autocomplete="new-password"
                          @input="markPostgresDisconnected"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">SSL mode</label>
                        <div class="select-wrapper">
                          <select
                            v-model="postgresConnection.sslMode"
                            class="form-input"
                            @change="markPostgresDisconnected"
                          >
                            <option value="disable">Disable</option>
                            <option value="prefer">Prefer</option>
                            <option value="require">Require</option>
                            <option value="verify-ca">Verify CA</option>
                            <option value="verify-full">Verify full</option>
                          </select>
                          <div class="select-arrow">▼</div>
                        </div>
                      </div>
                      <div class="form-group connection-action">
                        <button
                          type="button"
                          class="btn btn-primary btn-compact"
                          :disabled="
                            isConnectingPostgres || !canConnectPostgres
                          "
                          @click="connectPostgreSQL"
                        >
                          {{
                            isConnectingPostgres
                              ? "Connecting..."
                              : postgresConnected
                              ? "Reconnect"
                              : "Test and connect"
                          }}
                        </button>
                      </div>
                    </div>
                    <div
                      v-if="postgresConnectionStatus.message"
                      class="inline-status"
                      :class="postgresConnectionStatus.type"
                    >
                      {{ postgresConnectionStatus.message }}
                    </div>
                  </div>

                  <label class="form-label"
                    >Database table <span class="required">*</span></label
                  >
                  <div
                    class="database-picker"
                    :class="{ selected: newAsset.databaseTable }"
                  >
                    <div class="database-picker-icon">
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <ellipse
                          cx="12"
                          cy="5"
                          rx="8"
                          ry="3"
                          stroke="currentColor"
                          stroke-width="1.8"
                        />
                        <path
                          d="M4 5v7c0 1.66 3.58 3 8 3s8-1.34 8-3V5M4 12v7c0 1.66 3.58 3 8 3s8-1.34 8-3v-7"
                          stroke="currentColor"
                          stroke-width="1.8"
                        />
                      </svg>
                    </div>
                    <div class="database-picker-copy">
                      <span class="database-picker-title">{{
                        newAsset.databaseTable || "No table selected"
                      }}</span>
                      <span class="database-picker-description">{{
                        newAsset.databaseTable
                          ? "Table selected and ready to use"
                          : "Browse tables and inspect sample data before choosing one"
                      }}</span>
                    </div>
                    <button
                      type="button"
                      class="btn btn-secondary btn-compact"
                      :disabled="!canBrowseDatabase"
                      @click="openDatabaseBrowser"
                    >
                      {{
                        newAsset.databaseTable
                          ? "Change table"
                          : "Browse tables"
                      }}
                    </button>
                  </div>
                  <!-- <div v-if="newAsset.endpoint">
                  <label class="form-label">
                    Endpoint URL <span class="required">*</span>
                  </label>
                  <input
                    v-model="newAsset.endpoint"
                    type="url"
                    :readonly="true"
                    placeholder="https://api.example.com/data/orders"
                    class="form-input"
                    required
                  />
                </div> -->
                </div>
              </div>
            </div>

            <!-- Advanced Settings -->
            <div class="form-section">
              <button
                type="button"
                class="section-toggle"
                @click="advancedOpen = !advancedOpen"
              >
                <span>Advanced Settings</span>
                <svg
                  class="toggle-icon"
                  :class="{ open: advancedOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <transition name="fade-slide">
                <div v-if="advancedOpen" class="advanced-panel">
                  <div class="advanced-settings-grid">
                    <div class="setting-card">
                      <div class="setting-copy">
                        <span class="setting-title"
                          >Requires authentication</span
                        >
                        <span class="setting-description"
                          >Attach a stored connector secret to this data
                          address.</span
                        >
                      </div>
                      <div class="segmented-control">
                        <button
                          type="button"
                          :class="{ active: newAsset.requiresAuth }"
                          @click="newAsset.requiresAuth = true"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          :class="{ active: !newAsset.requiresAuth }"
                          @click="newAsset.requiresAuth = false"
                        >
                          No
                        </button>
                      </div>
                    </div>

                    <div
                      v-if="newAsset.requiresAuth"
                      class="secret-panel full-width"
                    >
                      <div class="form-grid">
                        <div class="form-group">
                          <label class="form-label">authKey</label>
                          <input
                            v-model="newAsset.authKey"
                            type="text"
                            class="form-input"
                            placeholder="Authorization"
                          />
                        </div>
                        <div class="form-group">
                          <label class="form-label">secretName</label>
                          <div class="select-wrapper">
                            <select
                              v-model="newAsset.secretName"
                              class="form-input"
                              :disabled="isLoadingSecrets"
                            >
                              <option value="" disabled>
                                {{
                                  isLoadingSecrets
                                    ? "Loading secrets..."
                                    : "Select a secret"
                                }}
                              </option>
                              <option
                                v-for="secret in secretOptions"
                                :key="secret.id"
                                :value="secret.id"
                              >
                                {{ secret.name }}
                              </option>
                            </select>
                            <div class="select-arrow">▼</div>
                          </div>
                        </div>
                      </div>

                      <div class="secret-actions">
                        <button
                          type="button"
                          class="btn btn-secondary btn-compact"
                          @click="showSecretForm = !showSecretForm"
                        >
                          {{
                            showSecretForm ? "Cancel New Secret" : "Add Secret"
                          }}
                        </button>
                        <span
                          v-if="newAsset.secretName"
                          class="selected-secret"
                        >
                          Selected: {{ newAsset.secretName }}
                        </span>
                      </div>

                      <transition name="fade-slide">
                        <div v-if="showSecretForm" class="inline-secret-form">
                          <div class="form-grid">
                            <div class="form-group">
                              <label class="form-label">Secret name / ID</label>
                              <input
                                v-model="secretDraft.id"
                                type="text"
                                class="form-input"
                                placeholder="orders-api-token"
                              />
                            </div>
                            <div class="form-group">
                              <label class="form-label">Secret value</label>
                              <input
                                v-model="secretDraft.value"
                                type="password"
                                class="form-input"
                                placeholder="Token or API key value"
                                autocomplete="new-password"
                              />
                            </div>
                          </div>
                          <div class="secret-form-footer">
                            <button
                              type="button"
                              class="btn btn-primary btn-compact"
                              :disabled="isSavingSecret"
                              @click="createSecret"
                            >
                              {{
                                isSavingSecret
                                  ? "Saving Secret..."
                                  : "Save and Select Secret"
                              }}
                            </button>
                          </div>
                        </div>
                      </transition>

                      <div
                        v-if="secretStatus.message"
                        class="inline-status"
                        :class="secretStatus.type"
                      >
                        {{ secretStatus.message }}
                      </div>
                    </div>

                    <div class="setting-card">
                      <div class="setting-copy">
                        <span class="setting-title">HTTP method</span>
                        <span class="setting-description"
                          >Choose how the connector should call the
                          endpoint.</span
                        >
                      </div>
                      <div class="segmented-control">
                        <button
                          type="button"
                          :class="{ active: newAsset.method === 'GET' }"
                          @click="newAsset.method = 'GET'"
                        >
                          GET
                        </button>
                        <button
                          type="button"
                          :class="{ active: newAsset.method === 'POST' }"
                          @click="newAsset.method = 'POST'"
                        >
                          POST
                        </button>
                      </div>
                    </div>

                    <div v-if="newAsset.method === 'POST'" class="setting-card">
                      <div class="setting-copy">
                        <span class="setting-title">Proxy body</span>
                        <span class="setting-description"
                          >Forward POST body content through the data
                          plane.</span
                        >
                      </div>
                      <label class="toggle">
                        <input v-model="newAsset.proxyBody" type="checkbox" />
                        <span class="toggle-track"></span>
                      </label>
                    </div>

                    <div class="setting-card">
                      <div class="setting-copy">
                        <span class="setting-title"
                          >Proxy query parameters</span
                        >
                        <span class="setting-description"
                          >Forward query string parameters for GET and
                          POST.</span
                        >
                      </div>
                      <label class="toggle">
                        <input
                          v-model="newAsset.proxyQueryParams"
                          type="checkbox"
                        />
                        <span class="toggle-track"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Additional Attributes Metadata -->
            <!-- <div class="form-section">
            <div class="section-header">
              <div class="section-title-content">
                <h3 class="section-title">Additional Attributes Metadata</h3>
                <p class="section-description">
                  Define additional field attributes for your asset (optional)
                </p>
              </div>
              <button class="add-attribute-btn" @click="addAttribute">
                <svg class="btn-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
                Add Attribute
              </button>
            </div>

            <div v-if="attributes.length > 0" class="attributes-list">
              <div
                v-for="(attr, index) in attributes"
                :key="index"
                class="attribute-item"
              >
                <div class="attribute-inputs"> -->
            <!-- Field Name -->
            <!-- <div class="form-group">
                    <label class="form-label">Field Name</label>
                    <input
                      v-model="attr.fieldName"
                      type="text"
                      placeholder="e.g., customer_id"
                      class="form-input"
                    />
                  </div> -->

            <!-- Data Type -->
            <!-- <div class="form-group">
                    <label class="form-label">Data Type</label>
                    <div class="select-wrapper">
                      <select v-model="attr.dataType" class="form-input">
                        <option value="" disabled selected>Select type</option>
                        <option value="VARCHAR">VARCHAR</option>
                        <option value="INTEGER">INTEGER</option>
                        <option value="DATE">DATE</option>
                        <option value="DATETIME">DATETIME</option>
                        <option value="BOOLEAN">BOOLEAN</option>
                        <option value="DECIMAL">DECIMAL</option>
                        <option value="TEXT">TEXT</option>
                        <option value="BLOB">BLOB</option>
                        <option value="FLOAT">FLOAT</option>
                        <option value="DOUBLE">DOUBLE</option>
                        <option value="TIMESTAMP">TIMESTAMP</option>
                      </select>
                      <div class="select-arrow">▼</div>
                    </div>
                  </div> -->

            <!-- Value -->
            <!-- <div class="form-group">
                    <label class="form-label">Value</label>
                    <input
                      v-model="attr.value"
                      type="text"
                      placeholder="Sample value"
                      class="form-input"
                    />
                  </div> -->

            <!-- Size -->
            <!-- <div class="form-group">
                    <label class="form-label">Size</label>
                    <input
                      v-model="attr.size"
                      type="text"
                      placeholder="e.g., 255"
                      class="form-input"
                    />
                  </div> -->

            <!-- Description -->
            <!-- <div class="form-group">
                    <label class="form-label">Description</label>
                    <input
                      v-model="attr.description"
                      type="text"
                      placeholder="Field description"
                      class="form-input"
                    />
                  </div>
                </div>
                <button
                  class="remove-attribute-btn"
                  @click="removeAttribute(index)"
                  title="Remove attribute"
                >
                  <svg class="remove-icon" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div v-else class="empty-attributes">
              <div class="empty-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <path
                    d="M12 7v6l3 3"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
              </div>
              <h4>No attributes added</h4>
              <p>Click "Add Attribute" to define field metadata</p>
            </div>
          </div> -->
          </fieldset>

          <!-- Form Actions -->
          <div class="form-actions">
            <button
              class="btn btn-secondary"
              @click="$emit('update:modelValue', 'provider-assets')"
            >
              Cancel
            </button>
            <div v-if="!editAsset && !viewOnly">
              <button
                class="btn btn-primary"
                :disabled="isRegistering || !isFormValid"
                @click="registerAsset"
              >
                <span v-if="!isRegistering" class="btn-content">
                  <svg class="btn-icon" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  Create Asset
                </span>
                <span v-else class="btn-content loading">
                  <div class="spinner"></div>
                  Creating Asset...
                </span>
              </button>
            </div>
            <div v-else-if="!viewOnly">
              <button
                class="btn btn-primary"
                :disabled="isRegistering || !isFormValid"
                @click="editExistingAsset"
              >
                <span v-if="!isRegistering" class="btn-content">
                  <svg class="btn-icon" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  Edit Asset
                </span>
                <span v-else class="btn-content loading">
                  <div class="spinner"></div>
                  Editing New Asset...
                </span>
              </button>
            </div>
          </div>

          <!-- Status Message -->
          <transition name="fade-slide">
            <div
              v-if="registrationStatus.message"
              class="status-message"
              :class="registrationStatus.type"
            >
              <svg class="status-icon" viewBox="0 0 24 24" fill="none">
                <path
                  v-if="registrationStatus.type === 'success'"
                  d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
                <path
                  v-if="registrationStatus.type === 'error'"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              {{ registrationStatus.message }}
            </div>
          </transition>
        </div>
      </div>
    </div>
    <Dialog
      v-model:visible="showDatabaseModal"
      modal
      :style="{ width: 'min(1180px, 94vw)' }"
      :draggable="false"
      class="database-dialog"
    >
      <template #header>
        <div class="database-dialog-header">
          <div class="dialog-title-icon">▦</div>
          <div>
            <h2>Choose a {{ databaseItemLabel }}</h2>
            <p>
              Review the structure and sample records before adding it to your
              asset.
            </p>
          </div>
        </div>
      </template>
      <div class="database-browser">
        <aside class="table-browser-sidebar">
          <div class="table-search">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="m20 20-4-4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <input
              v-model="tableSearch"
              type="search"
              placeholder="Search tables..."
              aria-label="Search database tables"
            />
          </div>
          <div class="table-list-heading">
            <span>Available {{ databaseItemLabel }}s</span
            ><span>{{ filteredDatabaseTables.length }}</span>
          </div>
          <div v-if="isLoadingTables" class="browser-state compact">
            <div class="dark-spinner"></div>
            <span>Loading tables...</span>
          </div>
          <div
            v-else-if="tableLoadError"
            class="browser-state compact error-state"
          >
            <span>{{ tableLoadError }}</span
            ><button type="button" @click="loadDatabaseTables">
              Try again
            </button>
          </div>
          <div
            v-else-if="!filteredDatabaseTables.length"
            class="browser-state compact"
          >
            <span>{{
              tableSearch
                ? "No tables match your search."
                : `No ${databaseItemLabel}s found.`
            }}</span>
          </div>
          <div v-else class="table-list">
            <button
              v-for="table in filteredDatabaseTables"
              :key="getTableId(table)"
              type="button"
              class="table-list-item"
              :class="{
                active: getTableId(table) === getTableId(activeDatabase),
              }"
              @click="previewTable(table)"
            >
              <span class="table-item-icon">▦</span>
              <span class="table-item-copy"
                ><strong>{{ getTableName(table) }}</strong
                ><small>{{ getTableDescription(table) }}</small></span
              >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m9 18 6-6-6-6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </aside>
        <section class="table-preview-panel">
          <div v-if="isLoadingPreview" class="browser-state preview-empty">
            <div class="dark-spinner"></div>
            <h3>Loading preview...</h3>
          </div>
          <div
            v-else-if="previewLoadError"
            class="browser-state preview-empty error-state"
          >
            <h3>Preview unavailable</h3>
            <p>{{ previewLoadError }}</p>
            <button
              v-if="activeDatabase"
              type="button"
              @click="previewTable(activeDatabase)"
            >
              Try again
            </button>
          </div>
          <div
            v-else-if="!activeDatabaseTable"
            class="browser-state preview-empty"
          >
            <div class="empty-preview-icon">▦</div>
            <h3>Select a {{ databaseItemLabel }} to preview</h3>
            <p>
              Choose one from the list to inspect its fields and sample records.
            </p>
          </div>
          <template v-else>
            <h2 style="margin: 0 0 4px">{{ activeDatabase.name }}</h2>
            <p style="margin: 0 0 16px">{{ activeDatabase.description }}</p>
            <json-data-viewer
              :key="`${newAsset.databaseProvider}-${getTableId(
                activeDatabase
              )}`"
              :data="activeDatabaseTable"
              :rows-per-page="5"
              :page-size-options="[5, 10, 25, 50]"
            />
          </template>
        </section>
      </div>
      <template #footer
        ><div class="database-dialog-footer">
          <span v-if="activeDatabaseTable"
            ><strong>{{ activeDatabase.name }}</strong> will be used as the
            asset data source.</span
          ><span v-else>Select a table to continue.</span>
          <div>
            <button
              type="button"
              class="btn btn-secondary btn-compact"
              @click="showDatabaseModal = false"
            >
              Cancel</button
            ><button
              type="button"
              class="btn btn-primary btn-compact"
              :disabled="!activeDatabaseTable || isLoadingPreview"
              @click="confirmDatabaseTable"
            >
              Use this table
            </button>
          </div>
        </div></template
      >
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted } from "vue";
import axios from "axios";
const { appContext } = getCurrentInstance();
import { userService } from "../services/UserService";
import Dialog from "primevue/dialog";

//import { API_BASE_URL } from "../config";
const API_BASE_URL = appContext.config.globalProperties.$apiBase;
const props = defineProps({
  modelValue: { type: String },
  existingAsset: { type: Object, default: null },
  providerAssets: { type: Object, default: null },
  storeAssets: { type: Array, default: () => [] },
  objectId: { type: String, default: "" },
  user: { type: String, default: null },
  viewOnly: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "asset-created"]);
const user = userService.getCookieUser().given_name;
const dashButtonInfo = userService.getCookieUser();

const editAsset = ref(false);
const advancedOpen = ref(false);
const creatorOpen = ref(false);
const datesOpen = ref(false);
const formatsOpen = ref(false);
const pricingOpen = ref(false);
const privateOpen = ref(false);
const showDatabaseModal = ref(false);
const databaseTables = ref([]);
const tableSearch = ref("");
const activeDatabaseTable = ref(null);
const activeDatabase = ref(null);
const isLoadingTables = ref(false);
const tableLoadError = ref("");
const isLoadingPreview = ref(false);
const previewColumns = ref([]);
const previewRows = ref([]);

// MongoDB credentials live only while this component is open. They are never
// added to newAsset, which prevents passwords from being stored in the catalog.
const mongoConnection = ref({
  connectionUri: "",
  databaseName: "",
});
const mongoConnected = ref(false);
const isConnectingMongo = ref(false);
const mongoConnectionStatus = ref({ type: "", message: "" });

// PostgreSQL connection values are kept separate from newAsset for the same
// reason as MongoDB: catalog records must never contain database credentials.
const postgresConnection = ref({
  host: "",
  port: 5432,
  databaseName: "",
  schemaName: "public",
  username: "",
  password: "",
  sslMode: "prefer",
});
const postgresConnected = ref(false);
const isConnectingPostgres = ref(false);
const postgresConnectionStatus = ref({ type: "", message: "" });

// State
const newAsset = ref({
  id: "",
  name: "",
  assetVersion: "",
  title: "",
  description: "",
  creatorLegalName: dashButtonInfo?.given_name || "",
  creatorContactName: dashButtonInfo?.name || "",
  creatorEmail: dashButtonInfo?.email || "",
  creatorUrl: dashButtonInfo?.organizationUrl || "",
  creatorUid: "",
  created: new Date().toISOString().split("T")[0],
  updated: new Date().toISOString().split("T")[0],
  tags: "",
  format: "application/json",
  contentType: "application/json",
  dataSpaceName: "",
  dataSpaceId: "",
  price: "0",
  priceUnit: "OTP",
  priceCurrency: "EUR",
  privateNotes: "",
  dataAddressType: "HttpData",
  endpoint: "",
  databaseTable: "",
  // DLM Store remains the default so the existing behaviour is unchanged.
  databaseProvider: "dlm",
  requiresAuth: false,
  authKey: "Authorization",
  secretName: "",
  method: "GET",
  proxyBody: true,
  proxyPath: true,
  proxyQueryParams: true,
  existsInConnector: false,
  published: 0,
});
const attributes = ref([
  {
    fieldName: "",
    dataType: "",
    description: "",
    size: "",
    value: "",
  },
]);
const isRegistering = ref(false);
const registrationStatus = ref({ type: "", message: "" });
const secrets = ref([]);
const isLoadingSecrets = ref(false);
const isSavingSecret = ref(false);
const showSecretForm = ref(false);
const secretStatus = ref({ type: "", message: "" });
const secretDraft = ref({
  id: "",
  value: "",
});

const assetContext = {
  "@vocab": "https://w3id.org/edc/v0.0.1/ns/",
  edc: "https://w3id.org/edc/v0.0.1/ns/",
  dcat: "http://www.w3.org/ns/dcat#",
  dct: "http://purl.org/dc/terms/",
  foaf: "http://xmlns.com/foaf/0.1/",
  vcard: "http://www.w3.org/2006/vcard/ns#",
  xsd: "http://www.w3.org/2001/XMLSchema#",
  ds2: "https://example.org/ds2/vocab#",
  odps: "https://example.org/odps/vocab#",
  "data-theme": "http://publications.europa.eu/resource/authority/data-theme/",
};

const normalizeName = (name) => name.trim().toLowerCase();

const splitCsv = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const extractDateValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value["@value"]) return value["@value"];
  return "";
};

const getStoreAssetName = (asset) =>
  asset?.contentData?.properties?.["ds2:name"] ||
  asset?.contentData?.properties?.name ||
  asset?.name ||
  "";

const getStoreAssetId = (asset) =>
  asset?.contentData?.["@id"] || asset?.externalId || asset?.id || "";

const getSecretId = (secret) =>
  secret?.externalId ||
  secret?.contentData?.["@id"] ||
  secret?.contentData?.id ||
  secret?.id ||
  "";

const getSecretName = (secret) =>
  secret?.name ||
  secret?.contentData?.name ||
  secret?.contentData?.["@id"] ||
  secret?.externalId ||
  "";

const findDuplicateName = (name, currentId = "") => {
  const normalized = normalizeName(name || "");
  if (!normalized) return null;
  return props.storeAssets.find((asset) => {
    const assetName = normalizeName(getStoreAssetName(asset));
    if (!assetName) return false;
    const assetId = getStoreAssetId(asset);
    if (currentId && assetId && assetId === currentId) return false;
    return assetName === normalized;
  });
};

const buildAssetId = (name) => {
  const slug = name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-]/g, "");
  return slug || "asset";
};

const buildCreator = () => {
  const creator = {
    "ds2:legalName": newAsset.value.creatorLegalName.trim(),
    "ds2:contactName": newAsset.value.creatorContactName.trim(),
    "ds2:email": newAsset.value.creatorEmail.trim(),
    "ds2:url": newAsset.value.creatorUrl.trim(),
    "ds2:uid": newAsset.value.creatorUid.trim(),
  };
  const hasCreator = Object.values(creator).some((value) => value);
  return hasCreator ? creator : null;
};

const buildPricingPlan = () => {
  const pricing = {
    "ds2:price": newAsset.value.price.trim(),
    "ds2:unit": newAsset.value.priceUnit.trim(),
    "ds2:priceCurrency": newAsset.value.priceCurrency.trim(),
  };
  const hasPricing = Object.values(pricing).some((value) => value);
  return hasPricing ? pricing : null;
};
const getTableName = (table) =>
  table?.name || table?.tableName || table?.externalId || "Untitled table";

const getTableId = (table) =>
  table?.externalId || table?.id || table?.tableName || table?.name || "";

const getTableDescription = (table) =>
  table?.description || table?.schema || "Database table";

const filteredDatabaseTables = computed(() => {
  const query = tableSearch.value.trim().toLowerCase();
  if (!query) return databaseTables.value;
  return databaseTables.value.filter((table) =>
    `${getTableName(table)} ${getTableDescription(table)}`
      .toLowerCase()
      .includes(query)
  );
});

// Connection can be attempted only when both required MongoDB values exist.
const canConnectMongo = computed(
  () =>
    mongoConnection.value.connectionUri.trim() &&
    mongoConnection.value.databaseName.trim()
);

// PostgreSQL requires all identifying connection fields. Password is allowed
// to be empty so local trust-based PostgreSQL installations can still connect.
const canConnectPostgres = computed(
  () =>
    postgresConnection.value.host.trim() &&
    Number(postgresConnection.value.port) > 0 &&
    Number(postgresConnection.value.port) <= 65535 &&
    postgresConnection.value.databaseName.trim() &&
    postgresConnection.value.schemaName.trim() &&
    postgresConnection.value.username.trim()
);

// DLM needs no setup. External providers become browsable only after their
// backend connection test succeeds.
const canBrowseDatabase = computed(
  () =>
    newAsset.value.databaseProvider === "dlm" ||
    (newAsset.value.databaseProvider === "mongodb" && mongoConnected.value) ||
    (newAsset.value.databaseProvider === "postgresql" &&
      postgresConnected.value)
);

// MongoDB calls its data containers collections; DLM and PostgreSQL call them tables.
const databaseItemLabel = computed(() =>
  newAsset.value.databaseProvider === "mongodb" ? "collection" : "table"
);

// Clear source-specific state whenever the user switches database providers.
// This prevents a DLM table from accidentally appearing as a Mongo collection.
const resetSelectedDatabaseSource = () => {
  databaseTables.value = [];
  activeDatabase.value = null;
  activeDatabaseTable.value = null;
  newAsset.value.databaseTable = "";
  newAsset.value.endpoint = "";
  tableLoadError.value = "";
};

const changeDatabaseProvider = () => {
  resetSelectedDatabaseSource();
  mongoConnected.value = false;
  postgresConnected.value = false;
  mongoConnectionStatus.value = { type: "", message: "" };
  postgresConnectionStatus.value = { type: "", message: "" };
};

// Editing either MongoDB value invalidates the previous connection check.
const markMongoDisconnected = () => {
  mongoConnected.value = false;
  resetSelectedDatabaseSource();
  mongoConnectionStatus.value = { type: "", message: "" };
};

// Any connection-field edit requires PostgreSQL to be tested again. It also
// clears tables/endpoints obtained with the previous credentials.
const markPostgresDisconnected = () => {
  postgresConnected.value = false;
  resetSelectedDatabaseSource();
  postgresConnectionStatus.value = { type: "", message: "" };
};

// Keep request construction in one place so connect and preview always send
// the same PostgreSQL settings and field names.
const getPostgresConnectionPayload = () => ({
  host: postgresConnection.value.host.trim(),
  port: Number(postgresConnection.value.port),
  database_name: postgresConnection.value.databaseName.trim(),
  schema_name: postgresConnection.value.schemaName.trim(),
  username: postgresConnection.value.username.trim(),
  password: postgresConnection.value.password,
  ssl_mode: postgresConnection.value.sslMode,
});

// Test MongoDB and receive its collections in the same request. The backend
// deliberately does not persist the connection URI or its embedded password.
const connectMongoDB = async () => {
  if (!canConnectMongo.value) return;
  isConnectingMongo.value = true;
  mongoConnectionStatus.value = { type: "", message: "" };
  resetSelectedDatabaseSource();

  try {
    const response = await axios.post(
      `${API_BASE_URL}/database/mongodb/connect`,
      {
        connection_uri: mongoConnection.value.connectionUri.trim(),
        database_name: mongoConnection.value.databaseName.trim(),
      }
    );
    databaseTables.value = response.data?.collections || [];
    mongoConnected.value = true;
    mongoConnectionStatus.value = {
      type: "success",
      message: `Connected. Found ${databaseTables.value.length} collections.`,
    };
  } catch (error) {
    mongoConnected.value = false;
    mongoConnectionStatus.value = {
      type: "error",
      message: getErrorMessage(error, "Unable to connect to MongoDB."),
    };
  } finally {
    isConnectingMongo.value = false;
  }
};

// Test PostgreSQL and load the tables visible in the selected schema.
const connectPostgreSQL = async () => {
  if (!canConnectPostgres.value) return;
  isConnectingPostgres.value = true;
  postgresConnectionStatus.value = { type: "", message: "" };
  resetSelectedDatabaseSource();

  try {
    const response = await axios.post(
      `${API_BASE_URL}/database/postgresql/connect`,
      getPostgresConnectionPayload()
    );
    databaseTables.value = response.data?.tables || [];
    postgresConnected.value = true;
    postgresConnectionStatus.value = {
      type: "success",
      message: `Connected. Found ${databaseTables.value.length} tables.`,
    };
  } catch (error) {
    postgresConnected.value = false;
    postgresConnectionStatus.value = {
      type: "error",
      message: getErrorMessage(error, "Unable to connect to PostgreSQL."),
    };
  } finally {
    isConnectingPostgres.value = false;
  }
};

const loadDatabaseTables = async () => {
  isLoadingTables.value = true;
  tableLoadError.value = "";
  try {
    if (
      newAsset.value.databaseProvider === "mongodb" ||
      newAsset.value.databaseProvider === "postgresql"
    ) {
      // External database objects are returned during their connection test.
      if (!canBrowseDatabase.value) {
        throw new Error("Connect to the database before browsing its data.");
      }
      return;
    }

    // Existing DLM Store behaviour remains unchanged.
    const response = await axios.get(`${API_BASE_URL}/database/tables`);
    databaseTables.value = Array.isArray(response.data)
      ? response.data
      : response.data?.tables || [];
  } catch (error) {
    tableLoadError.value = getErrorMessage(
      error,
      "We couldn't load the database tables."
    );
  } finally {
    isLoadingTables.value = false;
  }
};

const openDatabaseBrowser = async () => {
  if (!canBrowseDatabase.value) return;
  showDatabaseModal.value = true;
  tableSearch.value = "";
  activeDatabase.value = null;
  activeDatabaseTable.value = null;
  if (!databaseTables.value.length) await loadDatabaseTables();
};

const previewTable = async (table) => {
  activeDatabase.value = table;
  activeDatabaseTable.value = null;
  isLoadingPreview.value = true;

  try {
    if (newAsset.value.databaseProvider === "mongodb") {
      // MongoDB calls use POST because the connection details must be sent in
      // the request body rather than exposed in a query string or URL.
      const response = await axios.post(
        `${API_BASE_URL}/database/mongodb/preview`,
        {
          connection_uri: mongoConnection.value.connectionUri.trim(),
          database_name: mongoConnection.value.databaseName.trim(),
          collection_name: getTableId(table),
          limit: 50,
        }
      );
      activeDatabaseTable.value = response.data || [];
    } else if (newAsset.value.databaseProvider === "postgresql") {
      // The backend uses Psycopg's sql.Identifier for these names, so schema
      // and table identifiers are safely quoted before the SELECT is executed.
      const response = await axios.post(
        `${API_BASE_URL}/database/postgresql/preview`,
        {
          ...getPostgresConnectionPayload(),
          schema_name:
            table.schema || postgresConnection.value.schemaName.trim(),
          table_name: getTableName(table),
          limit: 50,
        }
      );
      activeDatabaseTable.value = response.data || [];
    } else {
      const id = activeDatabase.value.id;

      const response = await axios.get(`${API_BASE_URL}/database/tables/${id}`);

      activeDatabaseTable.value = response.data || [];
    }
  } catch (error) {
    tableLoadError.value = getErrorMessage(
      error,
      "Unable to load data from this source."
    );
  } finally {
    isLoadingPreview.value = false;
  }
};

const confirmDatabaseTable = async () => {
  if (!activeDatabaseTable.value) return;
  const response = await axios.post(
    `${API_BASE_URL}/json-endpoints`,
    activeDatabaseTable.value
  );
  // PostgreSQL stores schema.table to keep the source unambiguous. Other
  // providers keep their existing display name behaviour.
  newAsset.value.databaseTable =
    newAsset.value.databaseProvider === "postgresql"
      ? getTableId(activeDatabase.value)
      : getTableName(activeDatabase.value);
  newAsset.value.endpoint = `${API_BASE_URL}/${response.data.endpoint}`;

  showDatabaseModal.value = false;
};

const formatPreviewValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const buildProperties = () => {
  const optionalText = (value) => {
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  };
  const tags = splitCsv(newAsset.value.tags);
  const creator = buildCreator();
  const pricingPlans = buildPricingPlan();
  const properties = {
    "ds2:assetVersion": optionalText(newAsset.value.assetVersion),
    "ds2:name": newAsset.value.name.trim(),
    "ds2:description": newAsset.value.description.trim(),
    "ds2:created": newAsset.value.created || undefined,
    "ds2:updated": newAsset.value.updated || undefined,
    "dct:created": newAsset.value.created
      ? { "@value": newAsset.value.created, "@type": "xsd:date" }
      : undefined,
    "dct:modified": newAsset.value.updated
      ? { "@value": newAsset.value.updated, "@type": "xsd:date" }
      : undefined,
    "ds2:tags": tags.length ? tags : undefined,
    "ds2:format": optionalText(newAsset.value.format),
    "edc:contenttype": optionalText(
      newAsset.value.contentType || newAsset.value.format
    ),
    "ds2:dataSpaceName": optionalText(newAsset.value.dataSpaceName),
    "ds2:dataSpaceId": optionalText(newAsset.value.dataSpaceId),
    "ds2:pricingPlans": pricingPlans || undefined,
  };

  if (creator) {
    properties["ds2:creatorName"] = creator;
  }

  return properties;
};

const buildPayload = (idOverride = "") => {
  const assetId = idOverride || buildAssetId(newAsset.value.name);
  const dataAddress = {
    type: "HttpData",
    baseUrl: newAsset.value.endpoint.trim(),
    method: newAsset.value.method || "GET",
  };

  if (newAsset.value.requiresAuth) {
    dataAddress.authKey = newAsset.value.authKey.trim() || "Authorization";
    dataAddress.secretName = newAsset.value.secretName.trim();
  }

  if (newAsset.value.method === "POST") {
    dataAddress.proxyBody = newAsset.value.proxyBody ? "true" : "false";
  }

  dataAddress.proxyQueryParams = newAsset.value.proxyQueryParams
    ? "true"
    : "false";

  return {
    "@context": assetContext,
    "@id": assetId,
    properties: buildProperties(),
    dataAddress,
  };
};

// Computed
const secretOptions = computed(() =>
  secrets.value
    .map((secret) => ({
      id: getSecretId(secret),
      name: getSecretName(secret),
    }))
    .filter((secret) => secret.id)
);

const isFormValid = computed(() => {
  return (
    newAsset.value.name.trim() &&
    (newAsset.value.dataAddressType === "Database"
      ? newAsset.value.databaseTable.trim()
      : newAsset.value.endpoint.trim()) &&
    newAsset.value.description.trim() &&
    (!newAsset.value.requiresAuth || newAsset.value.secretName.trim())
  );
});

// Methods
function goBack() {
  emit("back");
}

function getErrorMessage(error, fallback) {
  return (
    error.response?.data?.detail ||
    error.response?.data?.message ||
    error.message ||
    fallback
  );
}

function isConnectorSecretConflict(error) {
  const message = getErrorMessage(error, "");
  return (
    error.response?.status === 409 ||
    message.includes("ObjectConflict") ||
    message.toLowerCase().includes("already exist")
  );
}

async function loadSecrets() {
  if (!props.user) return;

  isLoadingSecrets.value = true;
  secretStatus.value = { type: "", message: "" };
  try {
    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${props.user}`,
      {
        objectType: {
          name: "Secrets",
        },
      },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 100,
          sortBy: "_id",
        },
      }
    );
    secrets.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    secretStatus.value = {
      type: "error",
      message: getErrorMessage(error, "Unable to load saved secrets."),
    };
  } finally {
    isLoadingSecrets.value = false;
  }
}

async function saveSecretToStore(id) {
  const secretStorePayload = {
    ownerId: props.user,
    externalId: id,
    objectType: { name: "Secrets", level: 0 },
    name: id,
    description: "Connector management secret",
    comments: "",
    version: "1.0",
    tags: [],
    metadata: {
      additionalProp1: {
        inConnector: true,
      },
    },
    contentType: "json",
    contentData: {
      "@id": id,
      name: id,
    },
  };

  const storeResponse = await axios.post(
    `${API_BASE_URL}/repository/insert`,
    secretStorePayload,
    {
      params: {
        "API-KEY": "C5CC3F19",
      },
      headers: {
        Accept: "application/json",
        "API-KEY": "C5CC3F19",
      },
    }
  );

  if (!storeResponse.data.acknowledged) {
    throw new Error(
      storeResponse.data.error || "Failed to save secret in DLM store."
    );
  }

  secrets.value = [...secrets.value, secretStorePayload];
  newAsset.value.secretName = id;
  showSecretForm.value = false;
  secretDraft.value = { id: "", value: "" };
}

async function createSecret() {
  const id = secretDraft.value.id.trim();
  const value = secretDraft.value.value.trim();

  if (!id || !value) {
    secretStatus.value = {
      type: "error",
      message: "Secret name/ID and value are required.",
    };
    return;
  }

  const existingSecret = secretOptions.value.find(
    (secret) => secret.id === id || secret.name === id
  );

  if (existingSecret) {
    newAsset.value.secretName = existingSecret.id;
    secretStatus.value = {
      type: "success",
      message:
        "Existing DLM secret selected. Connector secret was not created again.",
    };
    showSecretForm.value = false;
    secretDraft.value = { id: "", value: "" };
    return;
  }

  isSavingSecret.value = true;
  secretStatus.value = { type: "", message: "" };
  try {
    await axios.post(`${API_BASE_URL}/management/v3/secrets`, {
      "@id": id,
      value,
    });

    await saveSecretToStore(id);
    secretStatus.value = {
      type: "success",
      message: "Secret saved and selected.",
    };
  } catch (error) {
    if (isConnectorSecretConflict(error)) {
      const connectorMessage = getErrorMessage(
        error,
        "Secret already exists in connector."
      );
      try {
        await saveSecretToStore(id);
        secretStatus.value = {
          type: "error",
          message: `${connectorMessage} The existing connector secret was added to the DLM store and selected.`,
        };
      } catch (storeError) {
        secretStatus.value = {
          type: "error",
          message: `${connectorMessage} ${getErrorMessage(
            storeError,
            "Unable to add the existing connector secret to the DLM store."
          )}`,
        };
      }
      return;
    }

    secretStatus.value = {
      type: "error",
      message: getErrorMessage(error, "Unable to save the secret."),
    };
  } finally {
    isSavingSecret.value = false;
  }
}

onMounted(() => {
  loadSecrets();
});

watch(
  () => props.existingAsset,
  (newVal) => {
    if (newVal) {
      //newAsset.value = { ...newVal };
      console.log("New values are: ", newVal);
      const properties = newVal?.properties || {};
      const creator = properties["ds2:creatorName"] || {};
      const dataAddress = newVal?.dataAddress || {};

      newAsset.value.id = newVal["@id"] || newAsset.value.id || "";
      newAsset.value.name =
        properties["ds2:name"] || properties.name || newAsset.value.name || "";
      newAsset.value.assetVersion =
        properties["ds2:assetVersion"] || newAsset.value.assetVersion || "";
      newAsset.value.title =
        properties["dct:title"] || newAsset.value.title || "";
      newAsset.value.description =
        properties["ds2:description"] ||
        properties.description ||
        newAsset.value.description ||
        "";
      newAsset.value.creatorLegalName =
        creator["ds2:legalName"] || newAsset.value.creatorLegalName || "";
      newAsset.value.creatorContactName =
        creator["ds2:contactName"] || newAsset.value.creatorContactName || "";
      newAsset.value.creatorEmail =
        creator["ds2:email"] || newAsset.value.creatorEmail || "";
      newAsset.value.creatorUrl =
        creator["ds2:url"] || newAsset.value.creatorUrl || "";
      newAsset.value.creatorUid =
        creator["ds2:uid"] || newAsset.value.creatorUid || "";
      newAsset.value.created =
        properties["ds2:created"] ||
        extractDateValue(properties["dct:created"]) ||
        newAsset.value.created ||
        "";
      newAsset.value.updated =
        properties["ds2:updated"] ||
        extractDateValue(properties["dct:modified"]) ||
        newAsset.value.updated ||
        "";
      newAsset.value.tags = Array.isArray(properties["ds2:tags"])
        ? properties["ds2:tags"].join(", ")
        : properties["ds2:tags"] || newAsset.value.tags || "";
      newAsset.value.format =
        properties["ds2:format"] || newAsset.value.format || "";
      newAsset.value.contentType =
        properties["edc:contenttype"] || newAsset.value.contentType || "";
      newAsset.value.dataSpaceName =
        properties["ds2:dataSpaceName"] || newAsset.value.dataSpaceName || "";
      newAsset.value.dataSpaceId =
        properties["ds2:dataSpaceId"] || newAsset.value.dataSpaceId || "";
      newAsset.value.price =
        properties["ds2:pricingPlans"]?.["ds2:price"] ||
        newAsset.value.price ||
        "";
      newAsset.value.priceUnit =
        properties["ds2:pricingPlans"]?.["ds2:unit"] ||
        newAsset.value.priceUnit ||
        "";
      newAsset.value.priceCurrency =
        properties["ds2:pricingPlans"]?.["ds2:priceCurrency"] ||
        newAsset.value.priceCurrency ||
        "";
      newAsset.value.privateNotes = newAsset.value.privateNotes || "";
      newAsset.value.endpoint =
        dataAddress?.baseUrl || newAsset.value.endpoint || "";
      newAsset.value.dataAddressType =
        dataAddress?.type || newAsset.value.dataAddressType || "HttpData";
      if (newAsset.value.dataAddressType === "Database") {
        newAsset.value.databaseTable = dataAddress?.baseUrl || "";
        newAsset.value.endpoint = "";
      }
      newAsset.value.requiresAuth = Boolean(dataAddress?.secretName);
      newAsset.value.authKey =
        dataAddress?.authKey || newAsset.value.authKey || "Authorization";
      newAsset.value.secretName =
        dataAddress?.secretName || newAsset.value.secretName || "";
      newAsset.value.method =
        dataAddress?.method || newAsset.value.method || "GET";
      newAsset.value.proxyBody =
        dataAddress?.proxyBody === "false"
          ? false
          : Boolean(dataAddress?.proxyBody ?? true);
      newAsset.value.proxyPath =
        dataAddress?.proxyPath === "false"
          ? false
          : Boolean(dataAddress?.proxyPath ?? true);
      newAsset.value.proxyQueryParams =
        dataAddress?.proxyQueryParams === "false"
          ? false
          : Boolean(dataAddress?.proxyQueryParams ?? true);
      newAsset.value.published = properties["published"];
      const storeMatch = props.storeAssets.find(
        (asset) =>
          asset.id === props.objectId || asset.externalId === newAsset.value.id
      );
      if (storeMatch?.metadata?.additionalProp1?.inConnector !== undefined) {
        newAsset.value.existsInConnector = Boolean(
          storeMatch.metadata.additionalProp1.inConnector
        );
      }
      if (storeMatch?.metadata?.additionalProp1?.internalNotes) {
        newAsset.value.privateNotes =
          storeMatch.metadata.additionalProp1.internalNotes;
      }
      //const metadata = newVal?.properties?.metadata || {};
      //newAsset.value.productId = metadata.productId || newVal["@id"] || "";
      // newAsset.value.productVersion =
      //   newVal["properties"]["productVersion"] || "";
      // newAsset.value.tags = Array.isArray(newVal["properties"]["tags"])
      //   ? newVal["properties"]["tags"].join(", ")
      //   : newVal["properties"]["tags"] || "";
      //newAsset.value.dataHolder = metadata.dataHolder || "";
      //newAsset.value.creatorName = metadata.creatorName || "";
      //newAsset.value.contactEmail = metadata.contactEmail || "";
      //newAsset.value.contactUrl = metadata.contactUrl || "";
      //newAsset.value.created = metadata.created || "";
      //newAsset.value.format = newVal["properties"]["format"] || "";
      // newAsset.value.outputPortType =
      //   newVal["properties"]["outputPortType"] || "";
      // newAsset.value.domains = Array.isArray(metadata.domains)
      //   ? metadata.domains.join(", ")
      //   : metadata.domains || "";
      // newAsset.value.relevantDataspaces = Array.isArray(
      //   metadata.relevantDataspaces
      // )
      //   ? metadata.relevantDataspaces.join(", ")
      //   : metadata.relevantDataspaces || "";
      //newAsset.value.payable = Boolean(newVal["properties"]["payable"]);
      editAsset.value = true;

      // if (newVal.properties && newVal.properties.attributes) {
      //   attributes.value = [
      //     {
      //       fieldName: newVal.properties.attributes.fieldName,
      //       dataType: newVal.properties.attributes.dataType,
      //       description: newVal.properties.attributes.description,
      //       size: newVal.properties.attributes.size,
      //       value: newVal.properties.attributes.value,
      //     },
      //   ];
      // }
    }
  },
  { immediate: true }
);

// function addAttribute() {
//   attributes.value.push({
//     fieldName: "",
//     dataType: "",
//     description: "",
//     size: "",
//     value: "",
//   });
// }

// function removeAttribute(index) {
//   attributes.value.splice(index, 1);
// }

async function registerAsset() {
  if (!isFormValid.value) return;

  console.log("Registering asset with values: ", newAsset.value);

  if (newAsset.value.requiresAuth && !newAsset.value.secretName.trim()) {
    registrationStatus.value = {
      type: "error",
      message:
        "Select or create a secret before registering this authenticated asset.",
    };
    advancedOpen.value = true;
    return;
  }

  const duplicate = findDuplicateName(newAsset.value.name);
  if (duplicate) {
    registrationStatus.value = {
      type: "error",
      message: "An asset with this name already exists in the store.",
    };
    return;
  }

  //console.log("Exist is: ", exist);

  // Filter out completely empty attributes
  // const filteredAttributes = attributes.value.filter(
  //   (attr) =>
  //     attr.fieldName.trim() !== "" ||
  //     attr.dataType.trim() !== "" ||
  //     attr.description.trim() !== "" ||
  //     attr.size.trim() !== "" ||
  //     attr.value.trim() !== ""
  // );

  isRegistering.value = true;
  registrationStatus.value = { type: "", message: "" };
  try {
    // const payload = {
    //   name: newAsset.value.name,
    //   id: id,
    //   description: newAsset.value.description,
    //   endpoint: newAsset.value.endpoint,
    //   type: newAsset.value.type,
    //   //productId: productId,
    //   productVersion: newAsset.value.productVersion.trim(),
    //   tags: newAsset.value.tags
    //     .split(",")
    //     .map((item) => item.trim())
    //     .filter(Boolean),
    //   //dataHolder: newAsset.value.dataHolder.trim(),
    //   //creatorName: newAsset.value.creatorName.trim(),
    //   //contactEmail: newAsset.value.contactEmail.trim(),
    //   //contactUrl: newAsset.value.contactUrl.trim(),
    //   //created: newAsset.value.created,
    //   format: newAsset.value.format,
    //   outputPortType: newAsset.value.outputPortType,
    //   // domains: newAsset.value.domains
    //   //   .split(",")
    //   //   .map((item) => item.trim())
    //   //   .filter(Boolean),
    //   // relevantDataspaces: newAsset.value.relevantDataspaces
    //   //   .split(",")
    //   //   .map((item) => item.trim())
    //   //   .filter(Boolean),
    //   payable: Boolean(newAsset.value.payable),
    // };

    const payload = buildPayload();

    console.log(
      "The payload before adding attributes: ",
      JSON.stringify(payload)
    );

    // Add attributes if any
    // if (filteredAttributes.length > 0) {
    //   payload.assetAttributesMetadata = filteredAttributes;
    // }

    const assetStorePayload = {
      ownerId: props.user,
      externalId: payload["@id"] || `asset-${Date.now()}`,
      objectType: { name: "Asset", level: 0 },
      name: payload.properties["ds2:name"] || "Unnamed Asset",
      description: payload.properties["ds2:description"] || "",
      comments: payload.properties.comments || "",
      version: payload.properties.version || "1.0",
      tags: payload.properties["ds2:tags"] || [],
      metadata: {
        additionalProp1: {
          inConnector: Boolean(newAsset.value.existsInConnector),
          internalNotes: newAsset.value.privateNotes.trim(),
        },
      },
      contentType: "json",
      contentData: payload,
    };

    console.log("Final payload to be sent: ", assetStorePayload);

    console.log("Payload being sent: ", JSON.stringify(payload));

    const response = await axios.post(
      `${API_BASE_URL}/repository/insert`,
      assetStorePayload,
      {
        params: {
          "API-KEY": "C5CC3F19",
        },
        headers: {
          Accept: "application/json",
          "API-KEY": "C5CC3F19",
        },
      }
    );

    console.log("Response from asset store: ", response.data);

    if (response.data.acknowledged) {
      registrationStatus.value = {
        type: "success",
        message: "Asset registered successfully!",
      };

      // Reset form and notify parent
      setTimeout(() => {
        resetForm();
        emit("asset-created");
      }, 1500);
      //emit("update:modelValue", "subscriber");
    } else {
      throw new Error(response.data.error || "Failed to register asset");
    }
  } catch (error) {
    console.error("Error registering asset:", error);
    registrationStatus.value = {
      type: "error",
      message:
        error.response?.data?.message ||
        error.message ||
        "An error occurred while registering the asset",
    };
  } finally {
    isRegistering.value = false;
  }
}

async function editExistingAsset() {
  if (!isFormValid.value) return;

  if (newAsset.value.existsInConnector) {
    registrationStatus.value = {
      type: "error",
      message:
        "Assets can only be edited before they are published to the connector.",
    };
    return;
  }

  if (newAsset.value.requiresAuth && !newAsset.value.secretName.trim()) {
    registrationStatus.value = {
      type: "error",
      message:
        "Select or create a secret before saving this authenticated asset.",
    };
    advancedOpen.value = true;
    return;
  }

  try {
    const duplicate = findDuplicateName(newAsset.value.name, newAsset.value.id);
    if (duplicate) {
      registrationStatus.value = {
        type: "error",
        message: "Another asset with this name already exists in the store.",
      };
      return;
    }

    isRegistering.value = true;
    registrationStatus.value = { type: "", message: "" };
    // const id = newAsset.value.name
    //   .toLowerCase()
    //   .replace(/\s+/g, "-")
    //   .replace(/[^a-z0-9\-]/g, "");

    // const payload = {
    //   name: newAsset.value.name,
    //   id: newAsset.value.id,
    //   description: newAsset.value.description,
    //   endpoint: newAsset.value.endpoint,
    //   type: newAsset.value.type,
    //   published: newAsset.value.published,
    //   metadata: {
    //     productId: newAsset.value.productId.trim() || newAsset.value.id,
    //     productVersion: newAsset.value.productVersion.trim(),
    //     tags: newAsset.value.tags
    //       .split(",")
    //       .map((item) => item.trim())
    //       .filter(Boolean),
    //     dataHolder: newAsset.value.dataHolder.trim(),
    //     creatorName: newAsset.value.creatorName.trim(),
    //     contactEmail: newAsset.value.contactEmail.trim(),
    //     contactUrl: newAsset.value.contactUrl.trim(),
    //     created: newAsset.value.created,
    //     format: newAsset.value.format,
    //     outputPortType: newAsset.value.outputPortType,
    //     domains: newAsset.value.domains
    //       .split(",")
    //       .map((item) => item.trim())
    //       .filter(Boolean),
    //     relevantDataspaces: newAsset.value.relevantDataspaces
    //       .split(",")
    //       .map((item) => item.trim())
    //       .filter(Boolean),
    //     payable: Boolean(newAsset.value.payable),
    //   },
    // };

    // Add attributes if any
    // if (filteredAttributes.length > 0) {
    //   payload.assetAttributesMetadata = filteredAttributes;
    // }

    const payload = buildPayload(newAsset.value.id);

    const assetStorePayload = {
      id: props.objectId,
      ownerId: props.user,
      externalId: payload["@id"],
      objectType: { name: "Asset", level: 0 },
      name: payload.properties["ds2:name"],
      description: payload.properties["ds2:description"] || "",
      comments: payload.properties.comments || "",
      version: payload.properties["ds2:assetVersion"] || "1.0",
      tags: payload.properties["ds2:tags"] || [],
      metadata: {
        additionalProp1: {
          inConnector: false,
          internalNotes: newAsset.value.privateNotes.trim(),
        },
      },
      contentType: "json",
      contentData: payload,
    };

    console.log("Payload being sent for update: ", assetStorePayload);

    const response = await axios.post(
      `${API_BASE_URL}/repository/update/full`,
      assetStorePayload,
      {
        params: {
          "API-KEY": "C5CC3F19",
        },
        headers: {
          Accept: "application/json",
          "API-KEY": "C5CC3F19",
        },
      }
    );
    if (response.data.acknowledged) {
      registrationStatus.value = {
        type: "success",
        message: "Asset updated successfully!",
      };

      setTimeout(() => {
        resetForm();
        emit("asset-created");
      }, 1500);
    } else {
      throw new Error(response.data.error || "Failed to update asset");
    }
  } catch (error) {
    console.error("Error updating asset:", error);
    registrationStatus.value = {
      type: "error",
      message:
        error.response?.data?.message ||
        error.message ||
        "An error occurred while updating the asset",
    };
  } finally {
    isRegistering.value = false;
  }
}

function resetForm() {
  newAsset.value = {
    id: "",
    name: "",
    assetVersion: "",
    title: "",
    description: "",
    creatorLegalName: "",
    creatorContactName: "",
    creatorEmail: "",
    creatorUrl: "",
    creatorUid: "",
    created: "",
    updated: "",
    tags: "",
    format: "",
    contentType: "",
    dataSpaceName: "",
    dataSpaceId: "",
    price: "",
    priceUnit: "",
    priceCurrency: "",
    privateNotes: "",
    dataAddressType: "",
    endpoint: "",
    databaseTable: "",
    databaseProvider: "dlm",
    requiresAuth: false,
    authKey: "Authorization",
    secretName: "",
    method: "GET",
    proxyBody: true,
    proxyPath: true,
    proxyQueryParams: true,
    existsInConnector: false,
    published: 0,
  };
  attributes.value = [];
  secretStatus.value = { type: "", message: "" };
  secretDraft.value = { id: "", value: "" };
  showSecretForm.value = false;
}
</script>

<style scoped>
.asset-fields {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.asset-fields:disabled {
  opacity: 0.85;
  pointer-events: none;
}

.asset-registration {
  height: 100%;
  padding: 0;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #009688 0%, #00695c 100%);
  color: white;
  padding: 1rem 0;
}

.header-content {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 2rem;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.back-icon {
  width: 18px;
  height: 18px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.title-icon {
  width: 70px;
  height: 70px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.title-icon svg {
  width: 32px;
  height: 32px;
}

.title-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  background: linear-gradient(135deg, #fff 0%, #e2e8f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-content p {
  margin: 0;
  font-size: 1.1rem;
  opacity: 0.9;
}

/* Main Container */
.registration-container {
  max-width: 100%;
  margin: 3rem auto 2rem;
  padding: 0 2rem;
}

.form-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  padding: 2.5rem 2.5rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.card-header h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.card-header p {
  color: #64748b;
  margin: 0;
  font-size: 1rem;
}

.form-content {
  padding: 0 2.5rem 2.5rem;
}

/* Form Sections */
.form-section {
  margin: 2.5rem 0;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 1.5rem 0;
}

.section-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #1e293b;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.section-toggle:hover {
  border-color: #009688;
  background: #f0fdfa;
}

.toggle-icon {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.toggle-icon.open {
  transform: rotate(180deg);
}

.advanced-panel {
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
}

.advanced-settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.advanced-settings-grid .full-width {
  grid-column: 1 / -1;
}

.setting-card,
.secret-panel {
  border: 1px solid #dbe5ef;
  border-radius: 10px;
  background: #f8fafc;
}

.setting-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 92px;
  padding: 1rem;
}

.setting-copy {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.setting-title {
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 700;
}

.setting-description {
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.4;
}

.segmented-control {
  display: inline-grid;
  grid-auto-flow: column;
  padding: 0.25rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: white;
  flex-shrink: 0;
}

.segmented-control button {
  min-width: 56px;
  height: 34px;
  padding: 0 0.75rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
}

.segmented-control button.active {
  background: #009688;
  color: white;
}

.secret-panel {
  padding: 1rem;
}

.secret-actions,
.secret-form-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.selected-secret {
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  word-break: break-word;
}

.inline-secret-form {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.btn-compact {
  min-height: 40px;
  padding: 0.65rem 1rem;
  font-size: 0.85rem;
}

.database-picker {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
  padding: 1rem;
  border: 1px solid #dbe5ef;
  border-radius: 14px;
  background: #f8fafc;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.database-provider-select {
  max-width: 420px;
  margin-bottom: 1rem;
}

.database-connection-card {
  margin: 0.5rem 0 1.25rem;
  padding: 1.1rem;
  border: 1px solid #dbe5ef;
  border-radius: 14px;
  background: #f8fafc;
}

.connection-card-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.connection-card-heading h4 {
  margin: 0 0 0.25rem;
  color: #1e293b;
  font-size: 1rem;
}

.connection-card-heading p {
  margin: 0;
  color: #64748b;
  font-size: 0.84rem;
}

.connection-badge {
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 0.72rem;
  font-weight: 700;
}

.connection-action {
  display: flex;
  align-items: flex-end;
  padding-bottom: 1px;
}

.database-coming-soon {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0.5rem 0 1.25rem;
  padding: 1rem;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.85rem;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.database-picker.selected {
  border-color: #99d8d1;
  background: #f0fdfa;
}

.database-picker-icon {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  border-radius: 12px;
  background: #dff7f3;
  color: #00796b;
}

.database-picker-icon svg {
  width: 24px;
  height: 24px;
}
.database-picker-copy {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 0.2rem;
}
.database-picker-title {
  color: #172033;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
}
.database-picker-description {
  color: #64748b;
  font-size: 0.84rem;
}

:deep(.database-dialog) {
  overflow: hidden;
  border: 0;
  border-radius: 18px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.25);
}

:deep(.database-dialog .p-dialog-header) {
  padding: 1.35rem 1.5rem;
  border-bottom: 1px solid #e8edf3;
}
:deep(.database-dialog .p-dialog-content) {
  padding: 0;
  background: #fff;
}
:deep(.database-dialog .p-dialog-footer) {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e8edf3;
  background: #fff;
}

.database-dialog-header {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}
.database-dialog-header h2 {
  margin: 0 0 0.2rem;
  color: #172033;
  font-size: 1.25rem;
}
.database-dialog-header p {
  margin: 0;
  color: #64748b;
  font-size: 0.88rem;
  font-weight: 400;
}
.dialog-title-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: #e5f7f4;
  color: #00796b;
  font-size: 1.4rem;
}

.database-browser {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  height: min(620px, 68vh);
}
.table-browser-sidebar {
  display: flex;
  min-height: 0;
  flex-direction: column;
  padding: 1rem;
  border-right: 1px solid #e8edf3;
  background: #f8fafc;
}
.table-search {
  position: relative;
}
.table-search svg {
  position: absolute;
  left: 0.8rem;
  top: 50%;
  width: 17px;
  height: 17px;
  color: #94a3b8;
  transform: translateY(-50%);
}
.table-search input {
  width: 100%;
  height: 42px;
  padding: 0 0.8rem 0 2.35rem;
  border: 1px solid #d7e0ea;
  border-radius: 10px;
  outline: none;
  background: #fff;
  color: #1e293b;
  font: inherit;
}
.table-search input:focus {
  border-color: #009688;
  box-shadow: 0 0 0 3px rgba(0, 150, 136, 0.1);
}
.table-list-heading {
  display: flex;
  justify-content: space-between;
  padding: 1.1rem 0.35rem 0.6rem;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.table-list-heading span:last-child {
  display: grid;
  place-items: center;
  min-width: 23px;
  height: 20px;
  border-radius: 10px;
  background: #e2e8f0;
  color: #475569;
}
.table-list {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.35rem;
  overflow-y: auto;
}
.table-list-item {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: 0.18s ease;
}
.table-list-item:hover {
  border-color: #d7e0ea;
  background: #fff;
}
.table-list-item.active {
  border-color: #80cbc4;
  background: #e8f8f6;
  box-shadow: 0 2px 10px rgba(0, 121, 107, 0.08);
}
.table-item-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 8px;
  background: #e8edf3;
  color: #475569;
}
.table-list-item.active .table-item-icon {
  background: #bfe9e4;
  color: #00796b;
}
.table-item-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.15rem;
}
.table-item-copy strong,
.table-item-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.table-item-copy strong {
  color: #253044;
  font-size: 0.88rem;
}
.table-item-copy small {
  color: #7b8799;
  font-size: 0.75rem;
}
.table-list-item > svg {
  width: 16px;
  height: 16px;
  color: #94a3b8;
}

.table-preview-panel {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  padding: 1.5rem;
  background: #fff;
}
.preview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.preview-header h3 {
  margin: 0.2rem 0;
  color: #172033;
  font-size: 1.35rem;
}
.preview-header p {
  margin: 0;
  color: #64748b;
  font-size: 0.85rem;
}
.preview-eyebrow {
  color: #00897b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.preview-badge {
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}
.preview-notice {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 1rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid #bae6fd;
  border-radius: 9px;
  background: #f0f9ff;
  color: #0369a1;
  font-size: 0.78rem;
}
.preview-notice svg {
  width: 17px;
  height: 17px;
  flex: 0 0 auto;
}
.preview-notice code {
  padding: 0.08rem 0.25rem;
  border-radius: 4px;
  background: rgba(3, 105, 161, 0.08);
}
.data-preview-wrap {
  min-height: 0;
  margin-top: 1rem;
  overflow: auto;
  border: 1px solid #dfe6ee;
  border-radius: 11px;
}
.data-preview-table {
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
}
.data-preview-table th {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dfe6ee;
  background: #f8fafc;
  text-align: left;
}
.data-preview-table th span,
.data-preview-table th small {
  display: block;
}
.data-preview-table th span {
  color: #334155;
  font-size: 0.78rem;
  font-weight: 700;
}
.data-preview-table th small {
  margin-top: 0.15rem;
  color: #94a3b8;
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
}
.data-preview-table td {
  max-width: 260px;
  padding: 0.85rem 1rem;
  overflow: hidden;
  border-bottom: 1px solid #edf1f5;
  color: #475569;
  font-size: 0.82rem;
  text-overflow: ellipsis;
}
.data-preview-table tr:last-child td {
  border-bottom: 0;
}
.data-preview-table tbody tr:hover {
  background: #f8fffe;
}
.preview-footer-note {
  padding-top: 0.7rem;
  color: #94a3b8;
  font-size: 0.72rem;
  text-align: right;
}
.browser-state {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  color: #64748b;
  font-size: 0.85rem;
}
.browser-state.compact {
  flex-direction: column;
  padding: 2rem 0.5rem;
  text-align: center;
}
.browser-state h3 {
  margin: 0;
  color: #334155;
}
.browser-state p {
  max-width: 360px;
  margin: 0;
  line-height: 1.5;
  text-align: center;
}
.preview-empty {
  flex-direction: column;
}
.empty-preview-icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin-bottom: 0.4rem;
  border-radius: 18px;
  background: #eef7f6;
  color: #009688;
  font-size: 1.8rem;
}
.error-state {
  color: #b91c1c;
}
.error-state button {
  border: 0;
  background: transparent;
  color: #00796b;
  font-weight: 700;
  cursor: pointer;
}
.dark-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #d9e2ec;
  border-top-color: #009688;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.database-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #64748b;
  font-size: 0.82rem;
}
.database-dialog-footer > div {
  display: flex;
  gap: 0.65rem;
}

.inline-status {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
}

.inline-status.success {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.inline-status.error {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
  margin-left: 5px;
}

.required {
  color: #ef4444;
}

.form-input,
.form-textarea,
.select-wrapper select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: white;
  color: #1e293b;
  height: 50px;
  margin-top: 5px;
}

.form-input:focus,
.form-textarea:focus,
.select-wrapper select:focus {
  outline: none;
  border-color: #009688;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.select-wrapper {
  position: relative;
}

.select-wrapper select {
  appearance: none;
  cursor: pointer;
}

.select-arrow {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #64748b;
}

.input-hint {
  font-size: 0.8rem;
  color: #64748b;
}

.toggle {
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 54px;
  height: 30px;
  margin-top: 8px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-track {
  position: absolute;
  inset: 0;
  background: #e2e8f0;
  border-radius: 999px;
  transition: background 0.2s ease;
}

.toggle-track::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

.toggle input:checked + .toggle-track {
  background: #009688;
}

.toggle input:checked + .toggle-track::after {
  transform: translateX(24px);
}

/* Attributes Section */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-title-content {
  flex: 1;
}

.section-description {
  color: #64748b;
  margin: 0.25rem 0 0 0;
  font-size: 0.9rem;
}

.add-attribute-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #009688;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.add-attribute-btn:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

.attributes-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.attribute-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.attribute-inputs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  flex: 1;
}

.remove-attribute-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #fef2f2;
  color: #dc2626;
  border: 2px solid #fecaca;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin-top: -1.5rem;
  margin-right: -1.5rem;
}

.remove-attribute-btn:hover {
  background: #fecaca;
  transform: scale(1.05);
}

.remove-icon {
  width: 18px;
  height: 18px;
}

.empty-attributes {
  text-align: center;
  padding: 3rem 2rem;
  background: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  color: #64748b;
}

.empty-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 1rem;
  color: #94a3b8;
}

.empty-attributes h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #475569;
}

.empty-attributes p {
  margin: 0;
  font-size: 0.9rem;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #f1f5f9;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #009688 0%, #00695c 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-content.loading {
  opacity: 0.8;
}

.btn-icon {
  width: 18px;
  height: 18px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Status Message */
.status-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-radius: 10px;
  margin-top: 1.5rem;
  font-weight: 500;
}

.status-message.success {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.status-message.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.status-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* Animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .attribute-inputs {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .database-picker {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .database-picker .btn {
    width: 100%;
  }
  .database-browser {
    grid-template-columns: 1fr;
    height: min(680px, 72vh);
  }
  .table-browser-sidebar {
    max-height: 250px;
    border-right: 0;
    border-bottom: 1px solid #e8edf3;
  }
  .table-preview-panel {
    padding: 1rem;
    overflow: auto;
  }
  .database-dialog-footer {
    align-items: stretch;
    flex-direction: column;
  }
  .database-dialog-footer > div {
    justify-content: flex-end;
  }

  .header-content {
    padding: 0 1.5rem;
  }

  .header-title {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .title-content h1 {
    font-size: 2rem;
  }

  .registration-container {
    padding: 0 1.5rem;
    margin: -2rem auto 2rem;
  }

  .card-header,
  .form-content {
    padding: 2rem 1.5rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .advanced-settings-grid {
    grid-template-columns: 1fr;
  }

  .setting-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
  }

  .attribute-item {
    flex-direction: column;
  }

  .attribute-inputs {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .remove-attribute-btn {
    align-self: flex-end;
    margin-top: 1rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .header-section {
    padding: 1.5rem 0;
  }

  .title-content h1 {
    font-size: 1.75rem;
  }

  .card-header h2 {
    font-size: 1.5rem;
  }

  .attribute-inputs {
    grid-template-columns: 1fr;
  }
}
</style>
